import { canCreateUsers } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createUser, getUsers } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { CreateUserCommonForm } from '@/routes/_layout/users/-components/create-user-common-form';
import {
  createUserCommonFormSchema,
  createUserOptionsFromFormData,
  nonCoreUserRolesFormSchema,
} from '@/routes/_layout/users/-components/form-schema';
import { NonCoreUserRolesForm } from '@/routes/_layout/users/-components/non-core-user-roles-form';
import { isFullAirAdmin } from '@airreading/common';
import { UserRoleAssignmentOptions } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

interface CreateUserProps {
  trigger?: ReactNode;
}

const formSchema = createUserCommonFormSchema
  .extend(nonCoreUserRolesFormSchema.shape)
  .extend({
    isAirAdmin: z.boolean().default(false),
    isAirLimitedAdmin: z.boolean().default(false),
  })
  .refine(({ isAirAdmin, isAirLimitedAdmin }) => !(isAirAdmin && isAirLimitedAdmin), {
    message: 'A user cannot be both an Air Admin and an Air Limited Admin',
    path: ['isAirLimitedAdmin'],
  });
type Form = z.infer<typeof formSchema>;

export const CreateUser = ({ trigger }: CreateUserProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: createUser,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();
  const canGrantAdminRoles = isFullAirAdmin(actingUser);

  const onSubmit = ({ formData, closeModal, reset }: SubmitProps<Form>) => {
    try {
      const payload = createUserOptionsFromFormData(formData);

      if (canGrantAdminRoles && formData.isAirAdmin) {
        payload.assignRoles?.push({
          role: 'air_admin',
          schoolId: null,
        });
      }

      if (canGrantAdminRoles && formData.isAirLimitedAdmin) {
        payload.assignRoles?.push({
          role: 'air_limited_admin',
          schoolId: null,
        });
      }

      if (formData.isSchoolAdmin) {
        if (!formData.adminSchools.length) {
          toast({
            title: 'Please select a school to assign school admin role',
          });
          return;
        }
        const schoolAdminRoles: UserRoleAssignmentOptions = formData.adminSchools.map(({ value }) => ({
          role: 'school_admin',
          schoolId: value,
        }));
        payload.assignRoles?.push(...schoolAdminRoles);
      }

      return mutateAsync(payload)
        .then(() => {
          toast({
            title: 'User created successfully',
          });
          closeModal();
          reset();
          queryClient.invalidateQueries(getUsers());
        })
        .catch((error) => {
          toast({
            title: airServerErrorFrom(error)?.userFacingMessage || 'Failed to create new user with unknown error',
          });
          console.error(error);
        });
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        toast({ title: error.message });
        return;
      }
    }
  };

  if (!canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Create User"
      formSchema={formSchema}
      trigger={trigger || <Button>Create other user</Button>}
      dialogContentProps={{ className: 'max-w-3xl' }}
      defaultValues={{ timeZone: undefined }}
      onSubmit={onSubmit}
    >
      {({ form }) => {
        return <CreateUserForm form={form} canGrantAdminRoles={canGrantAdminRoles} />;
      }}
    </FormModal>
  );
};

function CreateUserForm({ form, canGrantAdminRoles }: { form: UseFormReturn<Form>; canGrantAdminRoles: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2 py-4">
      <CreateUserCommonForm form={form} />
      {canGrantAdminRoles && (
        <div className="col-span-2 flex items-start gap-8">
          <FormCheckbox form={form} name="isAirAdmin" label="Is Air Admin" />
          <FormCheckbox form={form} name="isAirLimitedAdmin" label="Is Air Limited Admin" />
        </div>
      )}
      <NonCoreUserRolesForm form={form} />
    </div>
  );
}
